import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { writingAPI } from '@/lib/api';
import { Mic, Square, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import useMicRecorder from './useMicRecorder';

const C = { gold: '#D4A853', cream: '#F8F5EE', muted: '#94A3B8', green: '#34D399' };

// After an essay, two spoken questions about the child's own piece.
const WritingFollowup = ({ submissionId, onDone }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['writing-followup', submissionId],
    queryFn: async () => (await writingAPI.followup(submissionId)).data,
    staleTime: Infinity,
  });
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);

  const mic = useMicRecorder({
    maxSeconds: 60,
    onClip: async (blob, secs) => {
      setBusy(true);
      try {
        const { data: res } = await writingAPI.followupAnswer(submissionId, current, blob, secs);
        qc.setQueryData(['writing-followup', submissionId], (old) => ({ ...old, answered: res.answered, done: res.done }));
        if (res.done) onDone?.();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'That did not work. Please try again.');
      } finally {
        setBusy(false);
      }
    },
  });

  if (isLoading) return <p className="text-sm" style={{ color: C.muted }}>Getting your questions…</p>;
  if (!data?.eligible) return null;

  if (data.done) {
    return (
      <div className="p-3 rounded-xl mb-3 flex items-center gap-2 text-sm" style={{ background: 'rgba(52,211,153,0.12)', color: C.green }} data-testid="followup-done">
        <CheckCircle2 size={16} /> Thank you for telling us about your writing!
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 rounded-xl mb-3" style={{ background: 'rgba(212,168,83,0.08)', border: '1px solid rgba(212,168,83,0.3)' }} data-testid="writing-followup">
      <p className="font-bold text-sm mb-1" style={{ color: C.cream }}>One more thing: tell us about your writing!</p>
      <p className="text-xs mb-3" style={{ color: C.muted }}>Tap the microphone and answer out loud, in your own words. There are no wrong answers.</p>
      {mic.error && <p className="text-xs mb-2" style={{ color: '#F87171' }}>{mic.error}</p>}
      <div className="space-y-3">
        {data.questions.map((q, i) => {
          const answered = data.answered.includes(i);
          const mine = mic.recording && current === i;
          return (
            <div key={i} className="flex items-start gap-3">
              <button disabled={answered || busy || (mic.recording && !mine) || !mic.supported}
                onClick={() => { if (mine) { mic.stop(); } else { setCurrent(i); mic.start(); } }}
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 disabled:opacity-40"
                style={{ background: answered ? 'rgba(52,211,153,0.2)' : mine ? '#EF4444' : C.gold, color: answered ? C.green : '#1e1e2e' }}
                data-testid={`followup-mic-${i}`} aria-label={mine ? 'Stop recording' : 'Record answer'}>
                {answered ? <CheckCircle2 size={18} /> : mine ? <Square size={16} /> : <Mic size={18} />}
              </button>
              <div>
                <p className="text-sm" style={{ color: C.cream }}>{q}</p>
                {mine && <p className="text-xs" style={{ color: C.gold }}>Listening… {mic.seconds}s. Tap the square when you are done.</p>}
                {busy && current === i && <p className="text-xs" style={{ color: C.muted }}>Listening to your answer…</p>}
              </div>
            </div>
          );
        })}
      </div>
      {!mic.supported && <p className="text-xs mt-2" style={{ color: C.muted }}>This device cannot record. Ask a grown-up for help.</p>}
    </div>
  );
};

export default WritingFollowup;
